<template>
<div>
	<section v-if="participantsProp" class="form-section"
		:class="{'form-section-print-view': printView}">
		<h4>Participants</h4>

		<table class="table table-sm table-list mb-3">

			<tr>
				<th v-for="field in participantFields"> {{field.label}} </th>
				<th>Signature</th>
			</tr>

			<tr v-for="participant in mutatedParticipants">

				<!--- General participant fields --->
				<td v-for="field in participantFields">
					<span v-if="field.key in participant" v-html="participant[field.key]"></span>
				</td>

				<!--- Signature field --->
				<td class="d-flex flex-column align-items-center">

					<template v-if="'showSignatureButton' in participant && participant.showSignatureButton">

						<template v-if="'signature' in participant && participant.signature">

							<button type="button" class="btn btn-light btn-sm d-print-none" v-on:click="viewSignature(participant)">
								<i class="fa fa-eye"></i>
								View Signature
							</button>

							<signature-field
								class="d-none d-print-block"
								:hide-name="true"
								:readonly="true"
								:value="participant.signature">
							</signature-field>

						</template>

					</template>

				</td>
			</tr>
		</table>

	</section>

	<section v-for="(section,sectionIndex) in form" 
		v-if="sectionShouldBeShown(section) && section.fields.length > 0"
		:key="sectionIndex"
		class="form-section"
		:class="{'form-section-print-view': printView}">

		<h4>{{ section.sectionHeading }}</h4>

		<div v-for="(field,fieldIndex) in section.fields" class="field-container" v-if="fieldShouldBeShown(field)">

			<div class="field" :class="{
				'field-type-boolean' :          field.type == 'boolean',
				'field-type-table' :            field.type == 'tasklist' || field.type == 'table',
				'field-type-list' :             field.type == 'hazard-list' || field.type == 'control-list' || field.type == 'select-multi' || field.type == 'checklist',
				'field-type-medical-facility' : field.type == 'medical-facility-location',
				'field-type-weather' :          field.type == 'weather',
				'field-type-image' :            field.type == 'image',
				'field-type-text' :             field.type == 'text',
				'field-type-date' :             field.type == 'date',
				'field-type-time' :             field.type == 'time',
				'field-type-static-paragraph' : field.type == 'static-paragraph' || field.type == 'youtube-video',
				'field-type-library-file' :     field.type == 'library-file'
			}">

				<h5>{{ field.label }}</h5>

				<field v-if="field.type == 'hazard-list' || field.type == 'control-list' || field.type == 'select-multi' || field.type == 'checklist'"
					:readonly="true"
					:params="field"
					:value="field.value">
				</field>

				<div v-else-if="field.type == 'tasklist'">

					<div v-for="(item,itemIndex) in field.value" class="task-item">

						<field
							:key="field.label+Math.random()"
							label="Step/Task"
							col-class="col-3 col-sm-2"
							:readonly="true"
							:value="field.value[itemIndex]">
						</field>

						<table class="table table-sm table-bordered">
							<tr class="table-secondary">
								<th>Hazards</th>
								<th>Controls</th>
							</tr>
							<tr>
								<td>
									<ul v-if="'meta' in field && 'tasks' in field.meta && field.meta.tasks && field.meta.tasks.length > itemIndex && 'hazards' in field.meta.tasks[itemIndex]">
										<li v-for="hazard in field.meta.tasks[itemIndex].hazards">{{hazard}}</li>
									</ul>
								</td>
								<td>
									<ul v-if="'meta' in field && 'tasks' in field.meta && field.meta.tasks && field.meta.tasks.length > itemIndex && 'controls' in field.meta.tasks[itemIndex]">
										<li v-for="control in field.meta.tasks[itemIndex].controls">{{control}}</li>
									</ul>
								</td>
							</tr>
						</table>
					</div>

				</div>

				<div v-else-if="field.type == 'boolean'">
					<i class="fa" :class="{'fa-times' : field.value == 0, 'fa-check': field.value == 1, 'fa-2x' : !printView}"></i>
				</div>

				<div v-else-if="field.type == 'medical-facility-location' && 'value' in field && field.value">
					<iframe 
						width="100%"
						height="350"
						frameborder="0" style="border:0"
						:src="'https://www.google.com/maps/embed/v1/directions?key='+$store.GoogleEmbedAPIkey+'&destination='+field.value+'&origin='+$store.project.lat+','+$store.project.long" allowfullscreen>
					</iframe>
				</div>

				<weather-widget v-else-if="field.type == 'weather'"
					:historical="field.value"
					:show-warnings="true">
				</weather-widget>

				<div v-else-if="field.type == 'image'" class="d-flex flex-row flex-wrap">
					<div v-for="image in field.value">
						<image-panel
							:value="image"
							:image-url="getThumbSrc(image)"
							:expand-url="getMainSrc(image)"
							:expand-on-click="true"
							:readonly="true">
						</image-panel>
					</div>
				</div>

				<div v-else-if="field.type == 'whiteboard'" class="d-flex flex-row flex-wrap">
					<div v-for="image in field.value" :style="{width: image.width + 'px'}">

						<image-panel
							:value="image"
							:full-width="true"
							:readonly="true">
						</image-panel>
					</div>
				</div>

				<div v-else-if="field.type == 'table'" class="mt-2">
					<table class="table table-sm table-bordered">

						<tr v-if="'columns' in field && field.columns">
							<th v-if="'rows' in field && field.rows" class="table-list-field table-list-field-column"></th>
							<th v-for="columnField in field.columns" class="table-list-field" scope="row">
								{{typeof(columnField) == 'object' ? columnField.text : columnField}}
							</th>
						</tr>

						<!--
						<tr v-for="(row, rowIndex) in field.value">
							<th v-if="'rows' in field && field.rows" class="table-list-field table-list-field-column">
								{{typeof(field.rows[rowIndex]) == 'object' ? field.rows[rowIndex].text : field.rows[rowIndex]}}
							</th>
							<td v-for="col in row" class="p-2">
								<pre class="pre-field" :class="{'mb-3': !printView}" v-html="col" readonly></pre>
							</td>
						</tr>
					-->

						<template v-if="'rows' in field && field.rows">
							<tr v-for="(rowLabel,rowIndex) in field.rows" class="table-list-field table-list-field-column">
								<th>
									{{ rowLabel }}
								</th>
								<td v-for="(colLabel,colIndex) in field.columns" class="p-2 bg-white">
									<pre v-if="field.value && field.value.length > rowIndex && field.value[rowIndex].length > colIndex" class="pre-field" :class="{'mb-3': !printView}" v-html="field.value[rowIndex][colIndex]" readonly></pre>
								</td>
							</tr>
						</template>
						<template v-else-if="field.value">
							<tr v-for="(row,rowIndex) in field.value" class="table-list-field table-list-field-column">
								<td v-for="col in row" class="p-2 bg-white">
									<pre class="pre-field" :class="{'mb-3': !printView}" v-html="col" readonly></pre>
								</td>
							</tr>
						</template>

					</table>

				</div>

				<div v-else-if="field.type == 'static-paragraph'" v-html="field.default" class="text-muted"></div>

				<div v-else-if="field.type == 'library-file'">
					<div v-if="field.value && typeof(field.value) == 'object'" class="mt-2">

						<file-list-item
							:key="field.value.id"
							:id="field.value.id"
							:filename="field.value.original_filename"
							:src="$store.sessionToken ? getMainSrc(field.value, 'files') : false"
							:thumb-src="getThumbSrc(field.value, 'files')"
							:non-editable="true">
						</file-list-item>

					</div>
				</div>

				<div v-else-if="field.type == 'youtube-video'" class="mb-3">
					<label class="col-form-label">{{field.label}}</label>
					<youtube-video :url="field.value"></youtube-video>
				</div>

				<div v-else-if="field.type == 'personnel-phone-list'" class="mb-3">
					<label class="col-form-label">{{field.label}}</label>

					<template v-if="jobUsers && jobUsers.find(user => user.id == field.value)">
						{{ jobUsers.find(user => user.id == field.value).first_name }} {{ jobUsers.find(user => user.id == field.value).last_name }}: {{ jobUsers.find(user => user.id == field.value).phone_mobile }}
					</template>
					<template v-else>[Unknown]</template>
				</div>

				<pre v-else
					class="pre-field" 
					:class="{'mb-3': !printView}"
					readonly 
					v-html="field.value">
				</pre>
			</div>

			<div class="conditional-remarks" v-if="showConditionalRemarks(field)">
				<label>{{field.conditionalRemarksLabel}}</label>
				<pre
					class="pre-field" 
					:class="{'mb-3': !printView}"
					readonly 
					v-html="field.conditionalRemarksResponseValue">
				</pre>
			</div>

			<div class="conditional-image" v-if="showConditionalImageField(field) && 'conditionalImageResponseValue' in field">
				<label>{{field.conditionalImageLabel}}</label>

				<div class="d-flex flex-row flex-wrap">
					<image-panel
						v-for="image in field.conditionalImageResponseValue"
						:value="image"
						:image-url="getThumbSrc(image)"
						:expand-url="getMainSrc(image)"
						:expand-on-click="true"
						:readonly="true">
					</image-panel>
				</div>

			</div>
		</div>
	</section>

	<modal-dialog v-if="sigModal.record"
		:modal-visible="sigModal.visible"
		modal-size="modal-sm"
		:title="'Signature for '+sigModal.record.name"
		:submit-visible="false"
		cancel-button-text="Done"
		v-on:closeModal="sigModal.visible = false">

		<div class="d-flex flex-column align-items-center">
			<div>{{ formatDateTimeToLocal(sigModal.record.created_at)}}</div>
			<signature-field :readonly="true" :value="sigModal.record.signature"></signature-field>
		</div>
	</modal-dialog>
</div>
</template>
<script type="text/javascript">
import Field from "@/components/Field"
import WeatherWidget from "@/components/super_components/WeatherWidget"
import FormMixins from "@/mixins/FormMixins"
import ImagePanel from "@/components/ImagePanel"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import SignatureField from "@/components/SignatureField"
import FileListItem from "@/components/FileListItem"
import YoutubeVideo from "@/components/YoutubeVideo"

export default {
	name: "FormStaticRender",
	components: {
		WeatherWidget,
		Field,
		ImagePanel,
		TableList,
		ModalDialog,
		SignatureField,
		FileListItem,
		YoutubeVideo
	},
	mixins: [FormMixins],
	props: [
		"form",
		"fullForm",
		"participantsProp",
		"hideNoValueFields",
		"printView"
	],
	data() {
		return {
			sigModal: {
				visible: false,
				signature: {},
				record: null
			}
		}
	},
	watch: {
		participantsProp() {
			this.participants = this.participantsProp
		}
	},
	computed: {
		jobUsers() {
			let job
			if (this.fullForm && "Service" in this.fullForm && this.fullForm.Service) 
				job = this.fullForm.Service
			else (this.fullForm && "Job" in this.fullForm && this.fullForm.Job)
				job = this.fullForm.Job

			if (job && "User" in job && job.User)
				return job.User 

			return false
		}
	},
	methods: {
		viewSignature(record) {
			this.sigModal.visible = true
			this.sigModal.record = record
		},
		sectionShouldBeShown(section) {
			let vm = this

			if (!this.hideNoValueFields)
				return true
			else {
				let show = false
				section.fields.forEach(field => {
					if (vm.fieldShouldBeShown(field))
						show = true
				})

				return show
			}
			return true
		},
		fieldShouldBeShown(field) {
			if (!this.hideNoValueFields)
				return true
			else {
				if ('value' in field && field.value)
					return true
				else
					return false
			}

		}
	}
}	
</script>

<style type="text/css" scoped>

	.table-list-field-column {
		width: 20%;
	}

	.table-list-field {
		background-color: #CBCDCF;
	}

	.table-list .btn {
		border-radius: 5px;
		text-transform: uppercase;
		font-size: 9pt;
	}

	.table-list .btn-light {
		background-color: transparent;
		border: 1px solid var(--brand-primary-color);
	}

	.form-section pre {
		margin-bottom: 0;
	}

	.form-section-print-view {
		border: 1px solid #e0e0e0;
		padding: 15px;
		padding-top: 0;
		background-color: white;
	}

	.form-section-print-view h4 {
		padding: 15px;
		background: #6c757d;
		color: white;
		margin-left: -15px;
		margin-right: -15px;
		text-decoration: none;
	}

	.form-section-print-view.highlighted-section h4 {
		background: #b1690c;
	}

	.form-section-print-view .field-container {
		border-bottom: 1px solid #e0e0e07a;
	}

	.form-section-print-view .field {
		padding-top: 10px;
		padding-bottom: 10px;
	}

	.form-section-print-view .field:last-child {
		border-bottom: none;
	}

	.form-section-print-view .field h5 {
		margin-bottom: 0;
	}

	.form-section-print-view .field.field-type-boolean,
	.form-section-print-view .field.field-type-text {
		display: flex;
	}

	.form-section-print-view .field.field-type-boolean h5,
	.form-section-print-view .field.field-type-text h5 {
		width: 80%;
	}

	.form-section-print-view .field.field-type-boolean > div,
	.form-section-print-view .field.field-type-text > div {
		width: 20%;
	}

	.form-section-print-view .field-type-list,
	.form-section-print-view .field-type-weather,
	.form-section-print-view .field-type-table {
		display: block;
	}

	.form-section-print-view .field-type-list h5,
	.form-section-print-view .field-type-weather h5,
	.form-section-print-view .field-type-table h5,
	.form-section-print-view .field-type-list.field > div,
	.form-section-print-view .field-type-weather.field > div,
	.form-section-print-view .field-type-table.field > div {
		display: block;
		width: 100%;
	}

	.form-section-print-view .pre-field {
		font-size: 110%;
		font-family: inherit;
		word-wrap: break-word;
		white-space: pre-wrap;
	}

	.form-section-print-view .conditional-remarks, .form-section-print-view .conditional-image {
		margin-bottom: 10px;
	}

	.form-section-print-view .conditional-remarks label, .form-section-print-view .conditional-image label {
		font-weight: bold;
	}

	@media print {
		pre {
			border: none!important;
		}

		article {
			background-color: white!important;
		}
	}
</style>